import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * PL-LINE OVERRIDE — how long a pressed "Next page" waits for its batch before handing
 * the link back to the browser (see loadNextPage). Long enough for a slow mobile
 * connection; short enough that a stalled request does not leave a dead link.
 */
const PRESS_TIMEOUT_MS = 10000;

/**
 * PL-LINE OVERRIDE — the `history.state` key under which the pages on screen are saved
 * when the shopper leaves (see #saveLoadedPages and #restoreLoadedPages).
 */
const LOADED_PAGES_STATE_KEY = 'plLoadedPages';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLAnchorElement | HTMLSpanElement} [viewMoreNext] - The view more next button. PL-LINE OVERRIDE: an `<a>` while there is a next page (snippets/product-grid.liquid).
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  // PL-LINE OVERRIDE — `| undefined`: only created when a card renders `ref="cardGallery"`,
  // which `pl-product-card` does not (see the `?.` calls below).
  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  /**
   * PL-LINE OVERRIDE — the next-page render that "Next page" and the scroll trigger share.
   * @type {Promise<Element | null> | null}
   */
  #nextPageRender = null;

  /**
   * PL-LINE OVERRIDE — set when a press of "Next page" could not load; the next press then
   * follows the link's href. Cleared as soon as a batch loads again, or the list changes.
   * @type {boolean}
   */
  #nextPressNavigates = false;

  /**
   * PL-LINE OVERRIDE — which list is on screen: raised by every filter or sort change
   * (#handleFilterUpdate). A page that was still loading for an earlier list is dropped
   * when it arrives, instead of being kept in `pages` and appended to the new list.
   * @type {number}
   */
  #listVersion = 0;

  /**
   * PL-LINE OVERRIDE — the saved entry #restoreLoadedPages is bringing back, while it runs.
   * @type {{ section: string, url: string, first: number, last: number, height: number, width: number } | null}
   */
  #restoreTarget = null;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);

    // PL-LINE OVERRIDE — back to your spot after Back (see #restoreLoadedPages).
    window.addEventListener('pagehide', this.#saveLoadedPages);
    this.#restoreLoadedPages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
    // PL-LINE OVERRIDE — see connectedCallback.
    window.removeEventListener('pagehide', this.#saveLoadedPages);
  }

  /**
   * PL-LINE OVERRIDE — remember which pages are on screen when the shopper leaves.
   *
   * Written into the current history entry, next to the scroll position that
   * scroll-container.js saves on the same event, and with the same guard: Back returns
   * to this entry, a new visit to the list gets a new one without it.
   *
   * A shopper who leaves while #restoreLoadedPages is still loading has not gone past the
   * spot being restored, so that spot is saved again rather than the pages loaded so far:
   * otherwise the next Back would restore only those, and land on the footer.
   */
  #saveLoadedPages = () => {
    const { grid, cards } = this.refs;

    if (!grid || !Array.isArray(cards) || cards.length === 0) return;

    const restoring = this.#restoreTarget?.url === location.href ? this.#restoreTarget : null;

    try {
      const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
      history.replaceState(
        {
          ...currentState,
          [LOADED_PAGES_STATE_KEY]: restoring ?? {
            section: this.sectionId,
            url: location.href,
            first: Number(cards[0]?.dataset.page),
            last: Number(cards[cards.length - 1]?.dataset.page),
            height: grid.offsetHeight,
            width: window.innerWidth,
          },
        },
        ''
      );
    } catch (_) {
      // replaceState can throw if the state object exceeds the browser's size limit
    }
  };

  /**
   * PL-LINE OVERRIDE — back to your spot.
   *
   * When the browser keeps the page in memory, Back shows it exactly as it was and none
   * of this runs. When it does not (more often on phones), the page is rendered again
   * with its first page only. The scroll position the browser then restores lies below
   * the end of that shorter list, and the shopper lands on the footer.
   *
   * So before that restore happens, the list takes back the height it had, which lets
   * the restore land where the shopper was, and the pages it held are loaded into it.
   * The height is only reserved at the same window width; at another width the layout
   * differs and the pages are still loaded, below whatever the browser restores. A list
   * that started on a later page (`?page=3`, then scrolled up) is left as it is.
   *
   * The reserved height goes below page 1's rows (`align-content: start`). Spread over
   * them instead, it stretched each row, so page 1's products stood where the shopper had
   * been until the missing pages arrived, and then everything jumped. A filter or sort
   * change stops the restore (#handleFilterUpdate): the pages it was loading belong to
   * the old list.
   *
   * A refresh keeps the state the entry had before it: Chrome gives the reloaded page the
   * history state from before `pagehide`, so what #saveLoadedPages writes on the refresh
   * itself never arrives (nor does the scroll position scroll-container.js saves there).
   * A refresh of a list the shopper just opened therefore starts at the top, while a
   * refresh after a Back that restored the list returns to that same spot.
   */
  async #restoreLoadedPages() {
    const saved = typeof history.state === 'object' && history.state !== null ? history.state[LOADED_PAGES_STATE_KEY] : null;
    const { grid, cards } = this.refs;

    if (!saved || !grid || !Array.isArray(cards) || cards.length === 0) return;
    if (saved.section !== this.sectionId || saved.url !== location.href) return;

    const first = Number(cards[0]?.dataset.page);
    const last = Number(cards[cards.length - 1]?.dataset.page);

    if (saved.first !== first || !(saved.last > last)) return;

    const listVersion = this.#listVersion;
    const reserveHeight = saved.width === window.innerWidth && saved.height > grid.offsetHeight;

    this.#restoreTarget = saved;

    if (reserveHeight) {
      grid.style.minHeight = `${saved.height}px`;
      grid.style.alignContent = 'start';
    }

    try {
      const missingPages = Array.from({ length: saved.last - last }, (_, index) => last + 1 + index);

      // Fetch them side by side, then append in order: each append takes its page from `pages`.
      await Promise.allSettled(missingPages.map((page) => this.#fetchSpecificPage(page)));

      for (let count = 0; count < missingPages.length; count++) {
        if (listVersion !== this.#listVersion) break;
        if (!(await this.#renderNextPage())) break;
      }
    } finally {
      if (this.#restoreTarget === saved) this.#endRestore(grid);
    }
  }

  /**
   * PL-LINE OVERRIDE — the restore is over: release the height it reserved.
   * @param {HTMLElement} grid
   */
  #endRestore(grid) {
    this.#restoreTarget = null;
    grid.style.minHeight = '';
    grid.style.alignContent = '';
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);
    // PL-LINE OVERRIDE — see the `finally` below.
    const listVersion = this.#listVersion;

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    // PL-LINE OVERRIDE — when the fetch threw, the promise above was never resolved
    // and whatever awaited it — #renderNextPage, and through it "Next page" — waited
    // forever. None of this method's callers await it, so the error is logged here,
    // with the page it concerns, rather than surfacing as a bare unhandled rejection.
    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch (error) {
      console.error(`[paginated-list] Could not load page ${page.page} of ${this.sectionId} (${page.url})`, error);
    } finally {
      // PL-LINE OVERRIDE — a fetch for an earlier list does not resolve: the render waiting
      // now belongs to the new list, and woken by this one it found no page, gave up, and left
      // "Next page" on screen with nothing loading. #handleFilterUpdate already released
      // whatever waited for the old list.
      if (listVersion === this.#listVersion) resolvePromise();
    }
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    // PL-LINE OVERRIDE — dropped if the list changed while it loaded (see #listVersion).
    const listVersion = this.#listVersion;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    if (listVersion !== this.#listVersion) return;
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * PL-LINE OVERRIDE — one next-page render at a time.
   *
   * The scroll trigger and "Next page" both start one. Horizon keeps a single
   * resolver for the pending fetch, so a render started while another waited took
   * it over and the first never finished — for a press, that meant focus never moved,
   * or nothing happened at all when the load failed. Sharing the render in flight
   * leaves one resolver, one append, and one answer for every caller.
   *
   * @returns {Promise<Element | null>} The first card appended, or null if none was.
   */
  #renderNextPage() {
    this.#nextPageRender ??= this.#appendNextPage().finally(() => {
      this.#nextPageRender = null;
    });

    return this.#nextPageRender;
  }

  /**
   * PL-LINE OVERRIDE — Horizon's #renderNextPage, renamed so the wrapper above can share it.
   * It now returns the first card it appended (null when it appended none, where Horizon
   * returned nothing), and keyboard focus on "Next page" follows the batch. The other
   * changes are marked inline.
   *
   * @returns {Promise<Element | null>} The first card appended, or null if none was.
   */
  async #appendNextPage() {
    const { grid } = this.refs;

    if (!grid) return null;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return null;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const listVersion = this.#listVersion;
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      // PL-LINE OVERRIDE — the list changed while this waited: `nextPage` was the old list's.
      if (listVersion !== this.#listVersion) return null;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return null;
    }

    const firstAppended = nextPageItemElements[0] ?? null;
    const link = this.refs.viewMoreNext;
    const focusWasOnLink = Boolean(link) && document.activeElement === link;

    grid.append(...nextPageItemElements);

    // PL-LINE OVERRIDE — bring `this.refs` up to date now. Component only starts the
    // MutationObserver that refreshes refs at idle; an append before that (a busy page, an
    // early press) left `refs.cards` on the old cards, so #getPage asked for the same page
    // again and it was appended twice. Horizon calls updatedCallback() this way itself
    // (volume-pricing-info.js).
    this.updatedCallback();

    // PL-LINE OVERRIDE — `?.`: the helper only exists when a card renders Horizon's
    // `ref="cardGallery"`, and `pl-product-card` does not. Unguarded, this threw after
    // every append and skipped everything below it: the next-page link sync and the
    // prefetch of the page after.
    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    // PL-LINE OVERRIDE — Horizon writes `?page=N` into the address bar here; this
    // store does not. To the shopper the listing is one list. A `?page=N` address
    // sends a refresh, or a Back the browser could not serve from its cache, to that
    // page alone: the products before it and the collection text are gone until the
    // shopper scrolls back up. And the analytics scripts watching history read each
    // batch as a new page view.

    this.#syncViewMoreLink();

    if (firstAppended) {
      // PL-LINE OVERRIDE — loading works again, so pressing "Next page" loads in place again.
      this.#nextPressNavigates = false;

      // PL-LINE OVERRIDE — a keyboard user on "Next page" carries on from the first new
      // product, whichever trigger loaded the batch. Left on the link, focus would sit a
      // whole batch below the screen and the next Tab would skip every new product. The
      // link may be gone by now (last page), which leaves focus on <body>.
      if (focusWasOnLink && (document.activeElement === link || document.activeElement === document.body)) {
        firstAppended.querySelector('a[href]')?.focus({ preventScroll: true });
      }
    }

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    return firstAppended;
  }

  /**
   * PL-LINE OVERRIDE — keep the crawlable next-page link honest.
   *
   * `snippets/product-grid.liquid` renders the `viewMoreNext` sentinel as a real
   * `<a href>` so pages 2+ of a listing are reachable without scrolling — the
   * crawl path Googlebot needs, and the fallback when this script never runs.
   * Once a page has been appended that href points at a page already on screen,
   * so it is advanced to the next one that isn't, and removed when the series is
   * exhausted. A plain `<span>` sentinel is left alone.
   */
  #syncViewMoreLink() {
    const link = this.refs.viewMoreNext;

    if (!(link instanceof HTMLAnchorElement)) return;

    const next = this.#getPage('next');

    if (next && this.#shouldUsePage(next)) {
      link.href = next.url.toString();
    } else {
      link.remove();
    }
  }

  /**
   * PL-LINE OVERRIDE — pressing "Next page" loads the next page here, as scrolling does.
   *
   * Wired with `on:click` in `snippets/product-grid.liquid`. The link keeps its real
   * href for crawlers and for visitors this script never reaches; a shopper who
   * presses it stays on the list. `aria-busy` marks the wait. Focus then moves to
   * the first new product (see #appendNextPage), unless the shopper has put it
   * somewhere else in the meantime, such as the search field.
   *
   * If nothing could be appended, or nothing arrived within PRESS_TIMEOUT_MS, the
   * link is handed back to the browser: the next press opens that page the ordinary
   * way, until a batch loads again. It does not navigate by itself — by then the
   * shopper may have changed a filter or opened a product, and a jump to the old
   * page would undo that. A modified click still opens the link in a new tab or window.
   *
   * @param {MouseEvent} event
   */
  async loadNextPage(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = this.refs.viewMoreNext;

    if (!(link instanceof HTMLAnchorElement) || this.#nextPressNavigates) return;

    event.preventDefault();

    const listUrl = location.href;
    /** @type {Element | null} */
    let firstNewCard = null;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;

    link.setAttribute('aria-busy', 'true');

    try {
      firstNewCard = await Promise.race([
        this.#renderNextPage(),
        new Promise((resolve) => {
          timer = setTimeout(() => resolve(null), PRESS_TIMEOUT_MS);
        }),
      ]);
    } finally {
      clearTimeout(timer);
      link.removeAttribute('aria-busy');

      if (!firstNewCard) {
        if (link.isConnected && location.href === listUrl) this.#nextPressNavigates = true;
      } else if (document.activeElement === document.body) {
        // Safari does not focus a link on click, and #appendNextPage only moves focus
        // that was on the link. Focus anywhere else was put there by the shopper.
        firstNewCard.querySelector('a[href]')?.focus({ preventScroll: true });
      }
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const listVersion = this.#listVersion;
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      // PL-LINE OVERRIDE — same as in #appendNextPage.
      if (listVersion !== this.#listVersion) return;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const currentScrollTop = getScrollTop();
    // PL-LINE OVERRIDE — measure the first card. Horizon measures `grid.firstElementChild`,
    // which is the inline <script> at the top of the grid in product-grid.liquid: it has no
    // box, so the scroll was never adjusted, the earlier page pushed the view down, and the
    // previous-page sentinel never left the screen to fire again. Scrolling up from
    // `?page=3` stopped after page 2. Still the case in Horizon upstream (2026-09-21).
    const firstElement = grid.querySelector(':scope > [ref="cards[]"]') ?? grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + currentScrollTop : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    // PL-LINE OVERRIDE — refs up to date now, same reason as in #appendNextPage.
    this.updatedCallback();

    // PL-LINE OVERRIDE — `?.`, same reason as in #appendNextPage.
    this.#aspectRatioHelper?.processNewElements();

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + getScrollTop();
      const heightDiff = newHeight - oldHeight;
      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    // PL-LINE OVERRIDE — no `?page=N` in the address bar, same reason as in
    // #renderNextPage.

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {Event} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    // PL-LINE OVERRIDE — `!eventSection ||`: Horizon let through an event from outside every
    // section, which is exactly where the header's predictive search sits, so each keystroke
    // there counted as a new list. This list's filters and sort, drawer included, are inside
    // its own section (checked on collection, search and vendor pages).
    if (!eventSection || eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.pages.clear();

    // PL-LINE OVERRIDE — a new list (see #listVersion).
    const listVersion = ++this.#listVersion;

    // PL-LINE OVERRIDE — a restore after Back stops with the old list (#restoreLoadedPages);
    // the height it reserved goes now, not when its last page arrives.
    if (this.#restoreTarget && this.refs.grid) this.#endRestore(this.refs.grid);

    // PL-LINE OVERRIDE — a new list: pressing "Next page" loads in place again.
    this.#nextPressNavigates = false;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // PL-LINE OVERRIDE — once the new list has rendered, watch its "Next page" afresh,
    // after every change. Horizon only did so when the grid's `data-last-page` changed.
    // After a sort, or a filter with as many pages, the scroll trigger kept watching what
    // the old list had left: nothing, once that list had reached its end and
    // #syncViewMoreLink had removed the link, or a link already on screen, which the
    // observer does not report again. Either way scrolling loaded nothing more. Observing
    // again reports the link as it is now, so a "Next page" on screen loads its batch.
    // `promise` settles when the section has rendered (facets.js puts it on every
    // update event it dispatches).
    const rendered = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
    Promise.resolve(rendered)
      .catch(() => {})
      .then(() => {
        if (!this.isConnected || listVersion !== this.#listVersion) return;

        this.updatedCallback();
        this.infinityScrollObserver?.disconnect();
        this.#observeViewMore();
        this.#fetchPage('next');
      });
  };
}
